import React, { useState, useEffect } from 'react'
import { Navigate } from 'react-router-dom'
import useAuthStore from '../stores/authStore'
import ModoPantallaBoton from '../components/layout/ModoPantallaBoton'
import { motion, AnimatePresence } from 'framer-motion'
import IlustracionAccionSocial from '../components/common/IlustracionAccionSocial'

const Login = () => {
  const [usuario, setUsuario] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showHelp, setShowHelp] = useState(false)

  const { login, isLoading, error, isAuthenticated, clearError } = useAuthStore()

  if (isAuthenticated) {
    return <Navigate to="/" replace />
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    clearError()
    await login(usuario.trim(), password.trim(), null)
  }

  return (
    <div className="min-h-screen flex flex-col lg:flex-row font-inter bg-[#fcfdfe]">
      <style dangerouslySetInnerHTML={{ __html: `
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');
        
        :root {
          --sr-green: #143223;
          --sr-green-light: #2e7d32;
        }

        .sr-left-panel {
          position: relative;
          background:
            radial-gradient(120% 80% at 85% 95%, rgba(247, 195, 95, 0.22) 0%, rgba(247, 195, 95, 0) 55%),
            linear-gradient(160deg, #143223 0%, #1a4a30 55%, #1f5f3a 100%);
        }

        .sr-input:focus {
          border-color: var(--sr-green-light);
          box-shadow: 0 0 0 4px rgba(46,125,50,0.1);
        }

        .logo-glow {
          filter: drop-shadow(0 0 20px rgba(255,255,255,0.2));
        }

        .text-glow {
          text-shadow: 0 2px 10px rgba(0,0,0,0.3);
        }
      `}} />

      {/* PANEL IZQUIERDO */}
      <div className="sr-left-panel lg:w-[50%] p-10 pb-[300px] lg:p-[56px_72px] lg:pb-[56px] flex flex-col justify-start text-white overflow-hidden relative min-h-[640px] lg:sticky lg:top-0 lg:h-screen lg:min-h-[680px] lg:self-start">
        <div className="relative z-10 w-full">
          <div className="flex items-center gap-6 mb-10 lg:mb-[4.5vh] logo-glow">
            {/* El logo es verde oscuro sobre transparente: sobre el panel verde no se veía. */}
            <div className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-3xl bg-white shadow-xl shadow-black/20 ring-4 ring-white/15">
              <img
                src="/logo-sr.png"
                alt="Logo San Roque"
                className="h-full w-full scale-[1.75] object-contain"
              />
            </div>
            <div className="pt-1">
              <h2 className="text-3xl font-black tracking-tight leading-none text-white text-glow">SAN ROQUE</h2>
              <span className="block text-sm font-bold opacity-70 tracking-[0.4em] mt-1 text-[#b6ffb8]">MUNICIPIO</span>
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <h1 className="text-6xl lg:text-[length:clamp(3.25rem,10.5vh,6rem)] font-black mb-5 leading-[0.88] tracking-tighter text-glow">
              Acción <br/><span className="text-[#b6ffb8]">Social</span>
            </h1>

            <p className="text-xl lg:text-2xl opacity-90 leading-snug max-w-[500px] font-medium tracking-tight text-glow">
              Cada vale, cada viaje y cada ayuda llega a una persona.
            </p>

            <p className="mt-5 inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-sm font-bold text-[#b6ffb8] ring-1 ring-white/15 backdrop-blur">
              <span aria-hidden="true">♥</span> Unidos para construir una ciudad más justa e inclusiva
            </p>
          </motion.div>
        </div>

        {/* Ilustración: pegada al pie del panel; el cielo es transparente y se funde con el degradado */}
        <motion.div
          aria-hidden="false"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.9, ease: 'easeOut' }}
          className="pointer-events-none absolute inset-x-0 bottom-0 z-[1]"
        >
          <IlustracionAccionSocial className="block h-auto w-full" />
        </motion.div>
      </div>

      {/* PANEL DERECHO */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12 relative bg-white">
        {/* El modo de pantalla también acá: quien entra desde la calle, con sol, lo
            necesita antes de poder leer el formulario. */}
        <div className="absolute right-4 top-4 z-20">
          <ModoPantallaBoton tone="dark" />
        </div>
        <motion.div 
          initial={{ opacity: 0, x: 30 }}
          animate={{ opacity: 1, x: 0 }}
          className="relative z-10 w-full max-w-[460px]"
        >
          <div className="flex flex-col items-center mb-14">
            <div className="w-24 h-24 flex items-center justify-center mb-8">
              <img src="/logo-sr.png" alt="Logo SR" className="w-full h-full object-contain scale-[2.5]" />
            </div>
            <h1 className="text-4xl font-black text-slate-900 tracking-tight mb-3">Bienvenido</h1>
            <p className="text-lg text-slate-500 text-center font-medium">
              Gestión de Programas Sociales
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-8">
            <div className="space-y-3">
              <label className="block text-xs font-black text-slate-400 uppercase tracking-[0.2em] ml-1">Usuario</label>
              <div className="relative group">
                <span className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-300 text-xl group-focus-within:text-[#2e7d32] transition-colors">👤</span>
                <input
                  type="text"
                  value={usuario}
                  onChange={(e) => setUsuario(e.target.value)}
                  className="sr-input w-full pl-14 pr-5 py-5 bg-slate-50/50 border border-slate-100 rounded-[24px] text-lg transition-all focus:outline-none focus:bg-white focus:border-[#2e7d32]/30 shadow-sm"
                  placeholder="admin"
                  required
                />
              </div>
            </div>

            <div className="space-y-3">
              <label className="block text-xs font-black text-slate-400 uppercase tracking-[0.2em] ml-1">Contraseña</label>
              <div className="relative group">
                <span className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-300 text-xl group-focus-within:text-[#2e7d32] transition-colors">🔒</span>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="sr-input w-full pl-14 pr-14 py-5 bg-slate-50/50 border border-slate-100 rounded-[24px] text-lg transition-all focus:outline-none focus:bg-white focus:border-[#2e7d32]/30 shadow-sm"
                  placeholder="••••••••"
                  required
                />
                <button 
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors p-2"
                >
                  {showPassword ? '🙈' : '👁️'}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between text-[15px] px-1 font-semibold">
              <label className="flex items-center gap-3 text-slate-500 cursor-pointer">
                <input type="checkbox" className="w-6 h-6 rounded-lg border-slate-200 text-[#2e7d32] focus:ring-[#2e7d32] transition-all cursor-pointer" />
                <span>Recordarme</span>
              </label>
              <button 
                type="button" 
                onClick={() => setShowHelp(true)}
                className="text-[#2e7d32] hover:text-[#143223] transition-colors"
              >
                ¿Olvidaste tu contraseña?
              </button>
            </div>

            {error && (
              <div className="p-5 bg-red-50 border border-red-100 rounded-[24px] flex items-center gap-4 text-red-600 text-sm font-bold shadow-sm">
                <span className="text-2xl">⚠️</span> {error}
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-5 bg-gradient-to-r from-[#143223] to-[#2e7d32] text-white font-black rounded-[24px] shadow-2xl shadow-green-900/10 hover:shadow-green-900/20 hover:-translate-y-1 transition-all active:scale-[0.98] disabled:opacity-50 text-xl tracking-tight"
            >
              {isLoading ? (
                <div className="flex items-center justify-center gap-3">
                  <div className="w-6 h-6 border-3 border-white/30 border-t-white rounded-full animate-spin" />
                  Verificando...
                </div>
              ) : (
                'Ingresar al Sistema'
              )}
            </button>
          </form>

          <div className="mt-16 flex justify-center items-center gap-3 text-sm text-slate-400 font-bold tracking-[0.2em] uppercase bg-slate-50 py-4 px-8 rounded-full">
            <span className="text-xl text-[#2e7d32]">🛡️</span>
            Acceso Seguro
          </div>
        </motion.div>
      </div>

      {/* MODAL DE AYUDA */}
      <AnimatePresence>
        {showHelp && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-6 bg-slate-900/40 backdrop-blur-sm"
          >
            <motion.div 
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-[40px] p-10 max-w-[500px] w-full shadow-2xl relative overflow-hidden"
            >
              <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-[#143223] to-[#2e7d32]" />
              
              <div className="flex flex-col items-center text-center">
                <div className="w-20 h-20 rounded-full bg-green-50 flex items-center justify-center text-4xl mb-6">
                  🆘
                </div>
                <h2 className="text-3xl font-black text-slate-900 mb-6">Ayuda con la cuenta</h2>
                
                <div className="space-y-6 text-left w-full bg-slate-50 p-8 rounded-[32px] border border-slate-100">
                  <div className="flex items-start gap-4">
                    <span className="text-2xl mt-1">👤</span>
                    <div>
                      <p className="text-xs font-black text-slate-400 uppercase tracking-widest mb-1">Administrador</p>
                      <p className="text-lg font-bold text-slate-800">Ricardo Fernández</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <span className="text-2xl mt-1">🏢</span>
                    <div>
                      <p className="text-xs font-black text-slate-400 uppercase tracking-widest mb-1">Área</p>
                      <p className="text-lg font-bold text-slate-800">Dirección de Modernización</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <span className="text-2xl mt-1">🕒</span>
                    <div>
                      <p className="text-xs font-black text-slate-400 uppercase tracking-widest mb-1">Horario de Atención</p>
                      <p className="text-lg font-bold text-slate-800">Lunes a Viernes: 7:00hs a 12:30hs</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <span className="text-2xl mt-1">📱</span>
                    <div>
                      <p className="text-xs font-black text-slate-400 uppercase tracking-widest mb-1">Celular de Contacto</p>
                      <a href="https://wa.me/3794579348" className="text-xl font-black text-[#2e7d32] hover:underline">3794579348</a>
                    </div>
                  </div>
                </div>

                <button 
                  onClick={() => setShowHelp(false)}
                  className="mt-10 w-full py-5 bg-slate-900 text-white font-black rounded-[24px] hover:bg-slate-800 transition-colors text-lg"
                >
                  Entendido
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default Login
